import React, {
  FunctionComponent,
  ReactElement,
  useEffect,
  useState,
} from "react";
import {
  GameCodes,
  GameTitles,
  IdType,
  RoundHistoryComponentProps,
  RoundType,
  SpinHistory,
} from "../../type";
import { AnimationPoint, FeatureSymbols, VikingBlastGameData } from "./type";
import VikingBlastUtils from "./utils";

import "./viking-blast.scss";
import { Link, useLocation, useParams } from "react-router-dom";
import Utils from "../../utils";
import { useFocusSpinId } from "../../hooks";
import { BuyFreeSpinRoundHistoryView } from "../../components";
import BigNumber from "bignumber.js";
import { useTranslator } from "../../context/TranslationContext";

const lineRatio = BigNumber(0.05);
export const VikingBlastRoundHistoryView: FunctionComponent<
  RoundHistoryComponentProps
> = ({ roundHistory, type, id }) => {
  const focusSpinId = useFocusSpinId(type, id);
  const spin =
    roundHistory.spins.find((s) => s.id === focusSpinId) ??
    roundHistory.spins[0];
  const translator = useTranslator();
  if (!spin) return <>Spin not found</>;

  if (!spin.focus) spin.focus = true;

  const plays = roundHistory.spins.reduce((ps, s) => {
    if (!ps[s.playRefNumber]) ps[s.playRefNumber] = [];
    ps[s.playRefNumber].push(s);
    return ps;
  }, {} as { [k: string]: SpinHistory[] });
  return (
    <BuyFreeSpinRoundHistoryView
      gameCode={GameCodes.VikingBlast}
      roundHistory={roundHistory}
    >
      <div className="card mb-3 sb">
        <div className="card-body">
          <div className="card-title fs-4 fw-bold">
            {translator.get("spin")}
            <button
              type="button"
              className="btn btn-info float-end"
              data-bs-toggle="modal"
              data-bs-target="#explaination"
            >
              <i className="bi bi-info-circle fw-bold"></i>
            </button>
          </div>

          <hr />
          {Object.values(plays).map((s) => (
            <PlayRecord
              key={s[0].playRefNumber}
              spins={s}
              betAmount={+roundHistory.betAmount}
              onSpinClicked={(spinId) => {
                spin.focus = false;
              }}
              buyFreeSpinRate={
                roundHistory.buyFreeSpin ? roundHistory.buyFreeSpinRate : 1
              }
              roundType={roundHistory.roundType}
            />
          ))}
        </div>
      </div>
      <div id="explaination" className={`modal fade`} tabIndex={-1}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{translator.get("explanation")}</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="pb-3 fs-5">{translator.get("formula")}</div>
              <div className="pb-3">
                {translator.get("bet_size")} = {translator.get("bet")} x 0.05
              </div>
              <div className="pb-3">
                {translator.get("payout")} = {translator.get("bet_size")} x{" "}
                {translator.get("count")} x {translator.get("multiplier")}
              </div>
              <hr />
              <div className="pb-3 fs-5">{translator.get("multiplier")}</div>
              <table className="w-100">
                <tbody>
                  <tr>
                    {[
                      [201, 15],
                      [202, 8],
                      [203, 6],
                      [204, 4],
                    ].map(([symbol, payout], i) => (
                      <td key={`explanation-${symbol}`} className="text-center">
                        <div className="d-inline-block symbol">
                          {VikingBlastUtils.convertSymbol(+symbol)}
                        </div>
                        <div className="">{payout}</div>
                      </td>
                    ))}
                  </tr>
                  <tr>
                    {[
                      [101, 3],
                      [102, 2],
                      [103, 1],
                      [104, 1],
                    ].map(([symbol, payout], i) => (
                      <td key={`explanation-${symbol}`} className="text-center">
                        <div className="d-inline-block symbol">
                          {VikingBlastUtils.convertSymbol(+symbol)}
                        </div>
                        <div className="">{payout}</div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </BuyFreeSpinRoundHistoryView>
  );
};
const PlayRecord: FunctionComponent<{
  spins: SpinHistory[];
  betAmount: number;
  onSpinClicked: (id: string) => void;
  buyFreeSpinRate: number;
  roundType: RoundType;
}> = ({
  spins,
  betAmount: rawBetAmount,
  onSpinClicked,
  buyFreeSpinRate: rawBuyFreeSpinRate,
  roundType,
}) => {
  const translator = useTranslator();
  const betAmount = BigNumber(rawBetAmount);
  const buyFreeSpinRate = BigNumber(rawBuyFreeSpinRate);
  const firstSpin = spins[0];
  const payout = spins.reduce((t, s) => t.plus(+s.winAmount), BigNumber(0));
  const elements = [
    <div
      key={`play-${firstSpin.playRefNumber}-id`}
      className="list-group-item list-group-item-primary text-center border fs-4"
    >
      {firstSpin.mode === "Base"
        ? translator.get("normal_spin_id")
        : translator.get("free_spin_id")}
      : <span className="fw-bold">{firstSpin.playRefNumber}</span>
    </div>,
    <div
      key={`play-${firstSpin.playRefNumber}-header`}
      className="list-group-item list-group-item-secondary"
    >
      <div className="d-flex text-center">
        <div className="col-4 border">{translator.get("spin_count")}</div>
        <div className="col-2 border">{translator.get("bet_size")}</div>
        <div className="col-3 border">{translator.get("payout")}</div>
        <div className="col-3 border">{translator.get("profit")}</div>
      </div>
    </div>,
    <div
      key={`play-${firstSpin.playRefNumber}-content`}
      className="list-group-item text-center border border"
    >
      <div className="d-flex">
        <div className="col-4 border">{spins.length}</div>
        <div className="col-2 border">
          {Utils.asCurrency(
            betAmount.multipliedBy(lineRatio).dividedBy(buyFreeSpinRate),
            2
          )}
        </div>
        <div className="col-3 border">{Utils.asCurrency(payout)}</div>
        <div className="col-3 border">
          {Utils.asCurrency(
            firstSpin.reelMatrix.length > 1 &&
              firstSpin.mode === "Base" &&
              roundType !== RoundType.FreePlay
              ? payout.minus(betAmount).dividedBy(buyFreeSpinRate)
              : payout,
            2
          )}
        </div>
      </div>
    </div>,
  ] as ReactElement[];

  spins.forEach((s) => {
    elements.push(
      <SpinRecord key={s.id} spin={s} onSpinClicked={onSpinClicked} />
    );
  });
  return <div className="mb-2">{elements}</div>;
};

export const SpinRecord: FunctionComponent<{
  spin: SpinHistory;
  onSpinClicked: (id: string) => void;
}> = ({ spin, onSpinClicked }) => {
  const gameData = (spin.gameData as VikingBlastGameData) ?? {};
  const translator = useTranslator();
  let payoutElementList: ReactElement[] = [
    <div
      key={`spin-${spin.id}-header`}
      className="list-group-item list-group-item-secondary"
    >
      <div className="d-flex flex-wrap text-center">
        <div className="col-4 border">{translator.get("symbol")}</div>
        <div className="col-2 border">{translator.get("count")}</div>
        <div className="col-2 border">{translator.get("rate")}</div>
        <div className="col-4">{translator.get("payout")}</div>
      </div>
    </div>,
  ];
  if (gameData.triggeredFeatureSymbolId === FeatureSymbols.VikingBlast) {
    payoutElementList.push(
      <div key={`payout-${spin.id}-trigger-symbol`} className="d-flex">
        <div className="col-12 d-flex justify-content-center ">
          <div className="viking-blast">
            {VikingBlastUtils.convertSymbol(FeatureSymbols.VikingBlast)}
          </div>
        </div>
      </div>
    );
  } else if (
    gameData.triggeredFeatureSymbolId !== undefined &&
    gameData.triggeredFeatureSymbolId !== FeatureSymbols.None
  ) {
    const { symbolId, featureId } = VikingBlastUtils.cleanSymbolId(
      gameData.triggeredFeatureSymbolId
    );
    payoutElementList.push(
      <div key={`payout-${spin.id}-trigger-symbol`} className="d-flex">
        <div className="col-4 d-flex justify-content-center ">
          <div className="symbol">
            {VikingBlastUtils.convertSymbol(symbolId)}
          </div>
          {featureId ? (
            <div className="symbol">
              {VikingBlastUtils.convertSymbol(featureId)}
            </div>
          ) : (
            ""
          )}
        </div>
        <div className="col-4 ">{translator.get("bonus")}</div>
      </div>
    );
  } else if (!spin.payout || spin.payout.length === 0) {
    if (spin.reelMatrix.length === 0) {
      payoutElementList = [
        <div key={`payout-${spin.id}-trigger-symbol`} className="d-flex">
          <div className="col-12 col-md-12">
            <b>{translator.get("end")}</b>
          </div>
        </div>,
      ];
    } else if (
      spin.mode === "Base" &&
      (gameData.freeModeSymbolIds?.length || 0) > 0 &&
      gameData.freeSpinMultiplier > 0
    ) {
      payoutElementList.push(
        <div key={`payout-${spin.id}-trigger-symbol`} className="d-flex">
          <div className="col-12 col-md-12"> {translator.get("bonus")}</div>
        </div>
      );
    } else {
      payoutElementList.push(
        <div key={`payout-${spin.id}-trigger-symbol`} className="d-flex">
          <div className="col-12 col-md-12">{translator.get("end")}</div>
        </div>
      );
    }
  }
  const vikingBlastMultiplier =
    1 + +(gameData.triggeredFeatureSymbolId === FeatureSymbols.VikingBlast);
  payoutElementList.push(
    ...spin.payout
      .sort((a, b) => a.symbolId - b.symbolId)
      .map((p) => {
        const { symbolId, featureId } = VikingBlastUtils.cleanSymbolId(
          p.symbolId
        );
        return (
          <div key={`payout-${spin.id}-${p.symbolId}`} className="d-flex ">
            <div className="col-4 d-flex justify-content-center">
              <div className="symbol">
                {VikingBlastUtils.convertSymbol(symbolId)}
              </div>
              {featureId ? (
                <div className="symbol">
                  {VikingBlastUtils.convertSymbol(featureId)}
                </div>
              ) : (
                ""
              )}
            </div>
            <div className="col-2">x{p.count}</div>
            <div className="col-2">{p.payout * vikingBlastMultiplier}</div>
            <div className="col-4">{Utils.asCurrency(p.win)}</div>
          </div>
        );
      })
  );
  if (spin.payout.length > 0) {
    const total = spin.payout.reduce(
      (o, c) => {
        return { count: o.count + c.count, win: o.win + c.win };
      },
      { count: 0, win: 0 }
    );
    payoutElementList.push(
      <div className="px-4" key={`payout-${spin.id}-hr`}>
        <hr />
      </div>
    );
    payoutElementList.push(
      <div key={`payout-${spin.id}-total`} className="d-flex">
        <div className="col-4 fw-bold">{translator.get("win")}:</div>
        <div className="col-2 fs-6 fw-bold">{total.count}</div>
        <div className="col-2"></div>
        <div className="col-4 fs-6 fw-bold">
          {Utils.asCurrency(+spin.winAmount, 2)}
        </div>
      </div>
    );
  }
  let content = (
    <div className="d-flex flex-wrap text-center ">
      <div className="col-12 col-md-6  py-3">
        <Reel
          reelMatrix={spin.reelMatrix}
          points={gameData.animationPoints ?? []}
        />
      </div>
      <div
        className={`col-12 col-md-6 pb-3 ${
          spin.focus ? " list-group-item-success" : ""
        }`}
      >
        {payoutElementList}
      </div>
    </div>
  );
  if (!spin.reelMatrix || spin.reelMatrix.length === 0) {
    content = (
      <div className="d-flex flex-wrap text-center ">
        <div className="col-12 col-md-12 pb-3">{payoutElementList}</div>
      </div>
    );
  }

  return (
    <Link
      to={{ hash: spin.id }}
      id={spin.id}
      type="button"
      onClick={() => onSpinClicked(spin.id)}
      key={`spin-${spin.id}-id`}
      className={`list-group-item list-group-item-action border${
        spin.focus ? " border-success" : ""
      }`}
    >
      {content}
    </Link>
  );
};

export const Reel: FunctionComponent<{
  reelMatrix: number[][];
  points: AnimationPoint[];
}> = ({ reelMatrix, points }) => {
  const animatiopnMap = (points || []).reduce((map, point) => {
    const key = `${point.x}-${point.y}`;
    map[key] = point;
    return map;
  }, {} as Record<string, AnimationPoint>);

  const reelColumn = reelMatrix.reduce((col, rawCol, x) => {
    const colElements = rawCol.reduce((row, rawSymbol, y) => {
      const key = `${x}-${y}`;
      const animationPoint = animatiopnMap[key];
      row.push(
        <BoardCell key={key} symbol={rawSymbol} point={animationPoint} />
      );
      return row;
    }, [] as ReactElement[]);
    col.push(
      <div key={"col-" + x} className="d-flex flex-column">
        {colElements}
      </div>
    );
    return col;
  }, [] as ReactElement[]);

  return <div className="d-flex justify-content-center">{reelColumn}</div>;
};

const BoardCell: FunctionComponent<{
  symbol: number;
  point?: AnimationPoint;
}> = ({ symbol, point }) => {
  const classes = ["symbol", "flex", "border", "border-dark"];
  const { symbolId, featureId } = VikingBlastUtils.cleanSymbolId(symbol);
  let cellContent = VikingBlastUtils.convertSymbol(symbolId);
  const strFeatureId = featureId;
  const strSymbolId = symbolId;
  if (featureId === 1) {
    classes.push("ice");
  } else if (symbolId > 100 && featureId) {
    cellContent = VikingBlastUtils.convertSymbol(strSymbolId);
  } else if (symbolId < 100 && featureId) {
    classes.push("smaller");
    cellContent = [
      VikingBlastUtils.convertSymbol(strSymbolId, "1"),
      VikingBlastUtils.convertSymbol(strFeatureId, "2"),
    ];
  }
  if (point && point.groupId) {
    classes.push("animation-end");
  } else if (point && point.isDropdownPoint) {
    classes.push("animation");
  } else if (point && point.isIceBreak) {
    classes.push("break");
  }
  return <div className={classes.join(" ")}>{cellContent}</div>;
};
